import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { api } from '../../lib/api';
import {
  Loader2, Sparkles, TrendingUp, BarChart3, Target,
  ArrowUpRight, Award, Briefcase, ChevronRight
} from 'lucide-react';

const pct = (v) => `${(v ?? 0).toFixed(1)}%`;

const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
  if (score >= 60) return 'text-tp-accent bg-tp-accent/10 border-tp-accent/20';
  if (score >= 40) return 'text-orange-500 bg-orange-50 border-orange-200';
  return 'text-gray-400 bg-gray-50 border-gray-200';
};

const StrategicViewPage = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [trend, setTrend] = useState([]);
  const [smartMatches, setSmartMatches] = useState([]);
  const [missionsAnalyzed, setMissionsAnalyzed] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [catRes, trendRes, matchRes] = await Promise.all([
          api.get('/admin/reports/analytics/categories'),
          api.get('/admin/reports/analytics/weekly-trend?weeks=8'),
          api.get('/admin/analytics/top-smart-matches?limit_per_mission=3&max_missions=5'),
        ]);
        if (catRes.ok) {
          const data = await catRes.json();
          setCategories(data.categories || []);
        }
        if (trendRes.ok) {
          const data = await trendRes.json();
          setTrend(data.weeks || []);
        }
        if (matchRes.ok) {
          const data = await matchRes.json();
          setSmartMatches(data.matches || []);
          setMissionsAnalyzed(data.missions_analyzed || 0);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="strategic-view-loading">
        <Loader2 className="h-6 w-6 animate-spin text-tp-accent" />
      </div>
    );
  }

  const hasCategoryData = categories.some(c => c.total_candidates > 0);
  const topCategories = [...categories].sort((a, b) => (b.hired || 0) - (a.hired || 0)).slice(0, 5);
  const maxHired = Math.max(1, ...topCategories.map(c => c.hired || 0));

  const hasTrendData = trend.length > 0 && trend.some(w => (w.new_candidates || 0) + (w.evaluations || 0) + (w.hired || 0) > 0);
  const maxTrendVal = Math.max(1, ...trend.flatMap(w => [w.new_candidates || 0, w.evaluations || 0, w.hired || 0]));

  // Weekly totals for KPI header
  const totalCandidates = trend.reduce((s, w) => s + (w.new_candidates || 0), 0);
  const totalEvaluations = trend.reduce((s, w) => s + (w.evaluations || 0), 0);
  const totalHired = trend.reduce((s, w) => s + (w.hired || 0), 0);
  const globalConversion = totalCandidates > 0 ? (totalHired / totalCandidates) * 100 : 0;

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8" data-testid="strategic-view-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-tp-text flex items-center gap-2">
          <Target className="h-6 w-6 md:h-7 md:w-7 text-tp-accent" />
          Vue stratégique
        </h1>
        <p className="text-sm text-tp-text-light mt-1">
          Synthèse unifiée : analytics de conversion, évolution hebdo et meilleurs matchs candidats/missions.
        </p>
      </div>

      {/* KPI Summary (8-week totals) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="strategic-kpis">
        <Card className="p-4 border-tp-border">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-tp-text-light">Candidats (8 sem.)</span>
            <BarChart3 className="h-4 w-4 text-tp-primary" />
          </div>
          <div className="text-2xl font-semibold text-tp-text">{totalCandidates}</div>
        </Card>
        <Card className="p-4 border-tp-border">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-tp-text-light">Évaluations</span>
            <Award className="h-4 w-4 text-tp-accent" />
          </div>
          <div className="text-2xl font-semibold text-tp-text">{totalEvaluations}</div>
        </Card>
        <Card className="p-4 border-tp-border">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-tp-text-light">Embauchés</span>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </div>
          <div className="text-2xl font-semibold text-green-600">{totalHired}</div>
        </Card>
        <Card className="p-4 border-tp-border">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-tp-text-light">Conversion globale</span>
            <Target className="h-4 w-4 text-tp-accent" />
          </div>
          <div className={`text-2xl font-semibold ${globalConversion >= 20 ? 'text-green-600' : globalConversion >= 10 ? 'text-orange-500' : 'text-tp-text'}`}>
            {pct(globalConversion)}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Weekly trend */}
        <Card className="p-4 md:p-6 border-tp-border" data-testid="strategic-weekly-trend">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-tp-primary" />
            <h2 className="text-lg font-semibold text-tp-text">Évolution sur 8 semaines</h2>
          </div>
          {!hasTrendData ? (
            <p className="text-sm text-gray-400 italic text-center py-8">Pas encore de données pour tracer l'évolution.</p>
          ) : (
            <>
              <div className="flex items-end justify-between gap-2 h-40 px-2 pt-2 border-b border-gray-200 bg-gradient-to-b from-gray-50/50 to-transparent rounded-t-md">
                {trend.map((w) => {
                  const candH = ((w.new_candidates || 0) / maxTrendVal) * 100;
                  const evalH = ((w.evaluations || 0) / maxTrendVal) * 100;
                  const hireH = ((w.hired || 0) / maxTrendVal) * 100;
                  return (
                    <div key={w.week_start} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                      <div className="flex items-end gap-1 h-full w-full justify-center">
                        <div
                          className="w-3 bg-[#0F2744] rounded-t-sm transition-all hover:opacity-80"
                          style={{ height: `${candH}%`, minHeight: (w.new_candidates || 0) > 0 ? '4px' : '0' }}
                        />
                        <div
                          className="w-3 bg-[#4C8F6E] rounded-t-sm transition-all hover:opacity-80"
                          style={{ height: `${evalH}%`, minHeight: (w.evaluations || 0) > 0 ? '4px' : '0' }}
                        />
                        <div
                          className="w-3 bg-green-600 rounded-t-sm transition-all hover:opacity-80"
                          style={{ height: `${hireH}%`, minHeight: (w.hired || 0) > 0 ? '4px' : '0' }}
                        />
                      </div>
                      <div className="absolute bottom-full mb-1 bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                        <div>{w.week_start}</div>
                        <div>{w.new_candidates} cand · {w.evaluations} éval · {w.hired} emb</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 px-2 mt-1">
                {trend.map((w) => (
                  <span key={`${w.week_start}-label`} className="flex-1 text-center">{w.week_label}</span>
                ))}
              </div>
              <div className="flex items-center justify-center gap-4 mt-3 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#0F2744] rounded-sm" /> Candidats</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#4C8F6E] rounded-sm" /> Évaluations</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-green-600 rounded-sm" /> Embauchés</span>
              </div>
            </>
          )}
        </Card>

        {/* Top categories */}
        <Card className="p-4 md:p-6 border-tp-border" data-testid="strategic-top-categories">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-tp-accent" />
              <h2 className="text-lg font-semibold text-tp-text">Top catégories qui convertissent</h2>
            </div>
            <Link
              to="/admin/analytics/categories"
              className="text-xs text-tp-accent hover:underline flex items-center gap-1"
              data-testid="view-all-categories-link"
            >
              Tout voir <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
          {!hasCategoryData ? (
            <p className="text-sm text-gray-400 italic text-center py-8">
              Pas encore assez de données. Les taux s'afficheront dès les premières décisions clients et embauches.
            </p>
          ) : (
            <div className="space-y-3">
              {topCategories.map((c) => {
                const widthPct = c.hired > 0 ? (c.hired / maxHired) * 100 : 0;
                return (
                  <div key={c.category_id} className="flex items-center gap-3">
                    <span
                      className="inline-block px-2.5 py-1 rounded-md text-xs font-medium shrink-0 min-w-[130px]"
                      style={{ backgroundColor: `${c.color}1A`, color: c.color }}
                    >
                      {c.label_fr}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-tp-text-light">
                          {c.hired} embauché{c.hired > 1 ? 's' : ''} · {c.total_candidates} candidat{c.total_candidates > 1 ? 's' : ''}
                        </span>
                        <span className={`font-semibold ${c.overall_conversion >= 20 ? 'text-green-600' : c.overall_conversion >= 10 ? 'text-orange-500' : 'text-gray-400'}`}>
                          {pct(c.overall_conversion)}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${widthPct}%`, backgroundColor: c.color }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Smart matches across missions */}
      <Card className="p-4 md:p-6 border-tp-border" data-testid="strategic-smart-matches">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-tp-accent" />
            <h2 className="text-lg font-semibold text-tp-text">Matches intelligents — missions ouvertes</h2>
            {missionsAnalyzed > 0 && (
              <span className="text-xs text-tp-text-light">({missionsAnalyzed} mission{missionsAnalyzed > 1 ? 's' : ''} analysée{missionsAnalyzed > 1 ? 's' : ''})</span>
            )}
          </div>
          <Link
            to="/admin/missions"
            className="text-xs text-tp-accent hover:underline flex items-center gap-1"
            data-testid="view-all-missions-link"
          >
            Voir toutes les missions <ChevronRight className="h-3 w-3" />
          </Link>
        </div>

        {smartMatches.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-8">
            Aucune mission ouverte actuellement — les matchs apparaîtront ici dès qu'une mission sera active.
          </p>
        ) : (
          <div className="space-y-5" data-testid="strategic-matches-list">
            {smartMatches.map((m) => (
              <div key={m.mission_id} className="border border-gray-100 rounded-lg p-3 md:p-4 bg-gray-50/40">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                  <Link
                    to={`/admin/missions/${m.mission_id}`}
                    className="flex items-center gap-2 text-sm font-semibold text-tp-text hover:text-tp-accent transition-colors"
                    data-testid={`mission-link-${m.mission_id}`}
                  >
                    <Briefcase className="h-4 w-4 text-tp-primary" />
                    {m.mission_title}
                  </Link>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {m.detected_category_labels?.slice(0, 3).map((lbl) => (
                      <span key={lbl} className="px-2 py-0.5 bg-white border border-gray-200 text-gray-700 rounded-md text-[10px] font-medium">
                        {lbl}
                      </span>
                    ))}
                  </div>
                </div>
                {m.candidates.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Aucun candidat ne matche cette mission actuellement.</p>
                ) : (
                  <div className="space-y-2">
                    {m.candidates.map((c) => (
                      <Link
                        key={`${m.mission_id}-${c.candidate_id}`}
                        to={`/admin/candidates/${c.candidate_id}`}
                        className="flex items-center gap-3 p-2.5 rounded-md border border-gray-100 bg-white hover:border-tp-accent hover:shadow-sm transition-all"
                        data-testid={`strategic-match-${c.candidate_id}`}
                      >
                        <div className={`h-9 w-9 rounded-full border flex items-center justify-center font-bold text-xs shrink-0 ${getScoreColor(c.match_score)}`}>
                          {c.match_score}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-tp-text truncate">{c.name}</p>
                            {c.conversion_score >= 70 && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded-md font-semibold">
                                <TrendingUp className="h-2.5 w-2.5" /> {c.conversion_score}
                              </span>
                            )}
                            {c.qualification_score >= 70 && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-md font-semibold">
                                <Award className="h-2.5 w-2.5" /> {c.qualification_score}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{c.main_profession || '—'}</p>
                          {c.match_reasons?.length > 0 && (
                            <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                              • {c.match_reasons.slice(0, 2).join(' · ')}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* How to read */}
      <Card className="p-4 md:p-6 bg-blue-50/50 border-blue-100">
        <p className="text-sm text-blue-900 flex items-start gap-2">
          <ArrowUpRight className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            <strong>Comment lire :</strong> la Vue stratégique combine vos 3 sources de pilotage —
            <strong> évolution</strong> (volumes hebdo), <strong>conversion</strong> (efficacité par catégorie) et
            <strong> matching</strong> (meilleurs candidats pour vos missions ouvertes).
            Score ≥ 80 = excellent match · ≥ 60 = bon match · ≥ 40 = match partiel.
          </span>
        </p>
      </Card>
    </div>
  );
};

export default StrategicViewPage;
